import React, { useState, useEffect, useRef } from 'react'
import LocksTab from './LocksTab'
import FadesTab from './FadesTab'
import { T, TeamTag, SectionLabel, Spinner, SearchBar, pct, probColor, tierFromProb, Avatar, bookName, fmtOdds, tdColor, playColor, shownProb } from '../ui'
import { CompactPickRow, GameCard, isStrongCase } from '../cards'
import { NotifyCard } from './RecordTab'   // opt-in surfaced on Home to grow the push audience
import { shareNflSharpestSpots } from '../share'
import { WeekAtAGlance } from '../modals'
import AskBrkr from './AskBrkr'
import WeekDashboard from './WeekDashboard'
import { track } from '../track'
import { SlipCheckButton } from '../slipcheck'
import PrimeSpots from '../prime'
import { YourPlayers } from '../favorites.jsx'
import { getNFLLeaderboard, getNFLSlate, getNFLTierRecord, getNFLScorecard, getNFLSignals,
  getNFLResults, getNFLWeeklyRecord, getNFLCoaching, searchNFLPlayers, getNFLPlayerProps,
  getNFLSharpestSpots, getNFLMarketCalibration, getNFLRedZoneRecord, getNFLToday, getNFLAngles, NFL_MARKETS } from '../api'
import { playText, teamNick } from '../plain'

// Cross-market player search — find anyone by name and see EVERY market they're
// projected in this week (receptions, yards, TD, …) in one place, each tappable to
// the full bet breakdown. The one place to answer "what does the model think of X?".
function PlayerSearch({ season, week, onPlayer }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState([])          // name-match results
  const [teamHit, setTeamHit] = useState(null)  // {team, team_name} when the query was a team
  const [sel, setSel] = useState(null)          // selected player {name, team, opp, markets}
  const [loading, setLoading] = useState(false)
  const t = useRef(null)

  useEffect(() => {
    if (sel) return                              // showing a player's markets — don't re-search
    const ql = q.trim()
    if (ql.length < 2) { setHits([]); setTeamHit(null); return }
    clearTimeout(t.current)
    t.current = setTimeout(() => {
      searchNFLPlayers(ql, season, week).then(r => { setHits(r.players || []); setTeamHit(r.team ? { team: r.team, team_name: r.team_name } : null) })
    }, 220)                                      // debounce keystrokes
    return () => clearTimeout(t.current)
  }, [q, sel])

  const pick = p => {
    setLoading(true); setSel({ ...p, markets: null })
    getNFLPlayerProps(p.gsis_id, season, week).then(d => {
      setSel({ gsis_id: p.gsis_id, name: d.name || p.name, team: d.team || p.team,
               opp: d.opp, headshot: d.headshot, markets: d.markets || [] })
      setLoading(false)
    })
  }
  const clear = () => { setSel(null); setQ(''); setHits([]); setTeamHit(null) }

  return (
    <div style={{ marginBottom: 14 }}>
      {!sel && <SearchBar value={q} onChange={setQ} placeholder="Search a player or team (e.g. Bills)…" />}
      {/* name-match dropdown */}
      {!sel && hits.length > 0 && (
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', marginTop: -2 }}>
          {teamHit && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', borderBottom: `1px solid ${T.border}`, fontSize: 12.5, color: T.muted2 }}>
            <TeamTag abbr={teamHit.team} size={20} color={T.text} /> <span>· this week's players, best chance first</span></div>}
          {hits.slice(0, teamHit ? 12 : 8).map((p, i) => (
            <button key={p.gsis_id} onClick={() => pick(p)} style={{ display: 'flex', width: '100%', textAlign: 'left',
              alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'none',
              border: 'none', borderTop: i ? `1px solid ${T.border}` : 'none', color: T.text, padding: '10px 13px' }}>
              <span style={{ fontWeight: 700, fontSize: 13.5 }}>{p.name}</span>
              <span style={{ fontSize: 11, color: T.muted }}>{p.best ? <>{p.pos} · best: {p.best} <b style={{ color: T.text, fontFamily: T.mono }}>{Math.round((p.best_prob || 0) * 100)}%</b></> : [p.pos, teamNick(p.team)].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      )}
      {/* selected player's cross-market card */}
      {sel && (
        <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 12, padding: '12px 13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{sel.name}</div>
              <div style={{ fontSize: 11, color: T.muted }}>{[sel.team, sel.opp ? `vs ${teamNick(sel.opp)}` : ''].filter(Boolean).join(' · ')}</div>
            </div>
            <button onClick={clear} style={{ background: 'none', border: `1px solid ${T.border}`, color: T.muted,
              borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>← Search</button>
          </div>
          {loading ? <div style={{ fontSize: 12, color: T.muted, padding: '8px 0' }}>Loading markets…</div>
            : !sel.markets?.length ? <div style={{ fontSize: 12, color: T.muted, padding: '8px 0', lineHeight: 1.5 }}>
                No projected markets for {sel.name} this week — he may be below the model's volume threshold, or not on this week's slate.</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sel.markets.map((e, i) => {
                  const p = e.book_prob_over != null ? e.book_prob_over : (e.hit_prob != null ? e.hit_prob : e.prob_over) || 0
                  const ln = e.book_line != null ? e.book_line : e.line
                  const isTD = /td/i.test(e.market) && !/pass/i.test(e.market)
                  return (
                    <button key={i} onClick={() => onPlayer && onPlayer(e)} style={{ display: 'flex', width: '100%',
                      alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: T.card2,
                      border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 11px', color: T.text }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700 }}>{isTD || ln == null ? e.market : playText(e.market, ln)}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {e.edge_pct != null && Math.abs(e.edge_pct) >= 1 && (
                          <span style={{ fontSize: 10.5, fontWeight: 700, fontFamily: T.mono, color: e.edge_pct >= 0 ? T.green : T.red }}>
                            {e.edge_pct >= 0 ? '+' : ''}{e.edge_pct.toFixed(1)}%</span>)}
                        <span style={{ fontFamily: T.mono, fontWeight: 800, color: playColor(p, e) }}>{pct(p)}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
        </div>
      )}
    </div>
  )
}

// "Top-rated" = the high-conviction bands, the plays we'd stake the brand on.
const TOP_TIERS = new Set(['ELITE', 'BEST BET', 'STRONG'])
const topRated = tr => {
  const t = (tr?.tiers || []).filter(x => TOP_TIERS.has(x.tier))
  const plays = t.reduce((s, x) => s + x.plays, 0)
  const wins = t.reduce((s, x) => s + x.wins, 0)
  return plays >= 30 ? { plays, rate: Math.round((100 * wins) / plays) } : null
}
const CLV_MIN_N = 20                             // enough graded CLV samples before we headline it

// ── The model's top read — the hero. One card: the single best play, its hit %, and
// the tap that opens the full "why". This is the answer the home leads with.
function HeroRead({ e, onPlayer, still }) {
  if (!e) return null
  const priced = e.book_line != null && e.book_prob_over != null
  const prob = shownProb(e)
  const ln = e.book_line != null ? e.book_line : e.line
  const bet = e._isTD ? 'Anytime TD' : playText(e._mk, ln)
  const meta = [e.pos, e.team, e.opp ? `vs ${teamNick(e.opp)}` : ''].filter(Boolean).join(' · ')
  return (
    <button onClick={() => onPlayer && onPlayer(e)} style={{ display: 'block', width: '100%', textAlign: 'left',
      cursor: 'pointer', background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 14, padding: 14, marginBottom: 12, color: T.text }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span style={{ fontSize: 16.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.full_name}</span>
          </div>
          <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{meta}</div>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: T.text, background: T.card2,
            border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 8px', display: 'inline-block', marginTop: 8 }}>{bet}</span>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 30, lineHeight: 1, color: playColor(prob, e) }}>{pct(prob)}</div>
          <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.04em', marginTop: 3 }}>{e._isTD ? 'TO SCORE' : 'TO HIT'}{priced ? ' · BOOK' : ''}</div>
        </div>
      </div>
      {e.edge_pct != null && Math.abs(e.edge_pct) >= 1 && (
        <div style={{ marginTop: 10 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 7, padding: '4px 8px',
            color: e.edge_pct >= 0 ? T.green : T.red, background: e.edge_pct >= 0 ? '#0a2018' : T.redDim,
            border: `1px solid ${e.edge_pct >= 0 ? '#0c3d2a' : '#3d1414'}` }}>
            {e.edge_pct >= 0 ? '+' : ''}{e.edge_pct.toFixed(1)}% edge vs the book
          </span>
        </div>
      )}
      <div style={{ marginTop: 11, textAlign: 'center', fontSize: 11.5, fontWeight: 800, color: T.green,
        border: `1px solid ${T.greenMid}`, borderRadius: 9, padding: '8px 0' }}>Why this read →</div>
    </button>
  )
}

// ── Trust bar — the skinny front-page proof, on the way to the plays (not buried in a
// tab). Leads with live closing-line value once we have it, else the backtested
// top-rated hit rate; adds last settled week when there is one. Taps to the Record tab.
function TrustBar({ setView }) {
  const [top, setTop] = useState(null)
  const [clv, setClv] = useState(null)
  const [lastWk, setLastWk] = useState(null)
  const [rec, setRec] = useState(null)   // live season record of recommended plays (+ ROI)
  useEffect(() => { let a = true
    getNFLTierRecord(2026, 0).then(tr => { if (a) setTop(topRated(tr)) })
    getNFLScorecard(2026, 0).then(sc => { const r = sc?.realized
      const o = sc?.overall
      if (a && o && o.plays >= 20) setRec({ ...o, roi: sc.roi_pct })
      // Only headline CLV when it's a genuinely positive, well-sampled signal — advertising a
      // sub-50% beat-the-close rate would be anti-proof. Below the gate we fall back to the
      // backtested top-rated hit rate.
      if (a && r && r.clv_n >= CLV_MIN_N && (r.clv_positive_rate || 0) >= 52) setClv(r) })
    getNFLWeeklyRecord(2026).then(wr => { const wks = (wr?.weeks || []).filter(w => w.plays)
      if (a && wks.length) setLastWk(wks[wks.length - 1]) })
    return () => { a = false } }, [])
  const lw = lastWk ? ` · Week ${lastWk.week} went ${lastWk.wins}-${lastWk.losses}` : ''
  let txt
  if (rec) txt = <>Recommended plays: <b style={{ color: T.green, fontFamily: T.mono }}>{rec.wins}–{rec.losses}</b> ({rec.hit_rate}%){rec.roi != null ? <> · <b style={{ color: rec.roi > 0 ? T.green : T.muted2, fontFamily: T.mono }}>{rec.roi > 0 ? '+' : ''}{rec.roi}% ROI</b> at closing prices</> : null}{lw} · early record, every pick graded</>
  else if (clv) txt = <>Our plays beat the close <b style={{ color: T.green, fontFamily: T.mono }}>{clv.clv_positive_rate}%</b> of the time{lw}</>
  else if (top) txt = <>Top-rated plays hit <b style={{ color: T.green, fontFamily: T.mono }}>{top.rate}%</b> (backtested){lw}</>
  else txt = <>Model backtested 2022–25 — see the full record</>
  return (
    <button onClick={() => setView && setView('record')} style={{ display: 'flex', width: '100%', alignItems: 'center',
      justifyContent: 'space-between', gap: 8, cursor: 'pointer', textAlign: 'left', color: T.text,
      background: T.card3, border: '1px solid #113726', borderRadius: 11, padding: '10px 12px', marginBottom: 12 }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: T.green, marginBottom: 2 }}>{rec ? '✓ 2026 LIVE RECORD' : '✓ CLV-PROVEN · CALIBRATED'}</span>
        <span style={{ fontSize: 11.5, color: T.muted2, lineHeight: 1.4 }}>{txt}</span>
      </span>
      <span style={{ color: T.green, fontWeight: 900, flexShrink: 0 }}>→</span>
    </button>
  )
}

// ── Live status chips (game day) ─────────────────────────────────────────────
const LSTAT = {
  hit:      { label: '✓ HIT',      c: T.green,  bg: T.greenDim, bd: '#0c3d2a' },
  live:     { label: '● LIVE',     c: T.blue,   bg: T.blueDim,  bd: '#0e4b63' },
  miss:     { label: '✕ MISS',     c: T.red,    bg: T.redDim,   bd: '#3d1414' },
  push:     { label: 'PUSH',       c: T.amber,  bg: T.amberDim, bd: '#3d2f0a' },
  settling: { label: '◷ SETTLING', c: T.blue,   bg: T.blueDim,  bd: '#0e4b63' },
  pending:  { label: '⏳ PENDING',  c: T.muted,  bg: T.card2,    bd: T.border },
  dnp:      { label: 'DNP',        c: T.faint,  bg: T.card2,    bd: T.border },
}

// ── Live tracker (game day) — the featured board's hits locking in real time. Leads
// the home when any game is live; auto-refreshes off the This-Week results feed.
function LiveTracker({ results, onGame }) {
  const rec = results?.record || {}
  const plays = results?.plays || []
  const nLive = plays.filter(p => p.status === 'live' || p.status === 'settling').length
  // Totals come from the RECORD (counts every featured play), not the plays array (capped
  // at 100 for payload size) — otherwise "featured" caps at 100 while "pending" shows the
  // true count and the tiles don't reconcile.
  const nHit = rec.wins || 0
  const nFeatured = (rec.wins || 0) + (rec.losses || 0) + (rec.pushes || 0) + (rec.pending || 0)
  const shown = [...plays].sort((a, b) => {
    const order = { live: 0, settling: 1, hit: 2, miss: 3, push: 4, pending: 5, dnp: 6 }
    return (order[a.status] ?? 9) - (order[b.status] ?? 9)
  }).slice(0, 7)
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ background: 'linear-gradient(120deg,#08202b,#0b1119)', border: `1px solid #0e4b63`,
        borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
          <span style={{ fontSize: 13, fontWeight: 900, color: T.text }}>This week · live</span>
          <span style={{ fontSize: 10, fontWeight: 800, color: T.blue, background: T.blueDim, border: '1px solid #0e4b63', borderRadius: 6, padding: '2px 7px' }}>● updating</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {[[nHit, 'Hit ✅', T.green], [nLive, 'Live', T.blue], [rec.pending || 0, 'Pending', T.muted], [nFeatured, 'Featured', T.text]].map(([n, k, c], i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 20, lineHeight: 1, color: c }}>{n}</div>
              <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.04em', marginTop: 4, textTransform: 'uppercase' }}>{k}</div>
            </div>
          ))}
        </div>
      </div>
      {shown.map((p, i) => {
        const st = LSTAT[p.status] || LSTAT.pending
        const isTD = p.market === 'Anytime TD'
        const bet = isTD ? 'Anytime TD' : `${playText(p.market, p.line)}`
        const val = p.actual != null ? `went ${p.actual}` : p.live_actual != null ? `at ${p.live_actual}` : ''
        return (
          <button key={i} onClick={() => onGame && p.game_id && onGame({ game_id: p.game_id })}
            style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 9, cursor: p.game_id ? 'pointer' : 'default',
              background: T.card, border: `1px solid ${st.bd}`, borderRadius: 11, padding: '9px 11px', marginBottom: 7, color: T.text }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.2 }}>{p.name}</div>
              <div style={{ fontSize: 10.5, color: T.muted }}>{bet}{val ? <span style={{ color: p.live_actual != null ? T.blue : T.muted2 }}> · {val}</span> : ''}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 800, color: st.c, background: st.bg, border: `1px solid ${st.c}`,
              borderRadius: 7, padding: '4px 8px', whiteSpace: 'nowrap', flexShrink: 0 }}>{st.label}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Week recap (once every game is final) — the record leads: what hit, and a tap into
// the full card on the Record tab. Closes the loop between slates.
function RecapCard({ results, week, setView }) {
  const rec = results?.record || {}
  const graded = (rec.wins || 0) + (rec.losses || 0)
  const best = (results?.plays || []).filter(p => p.status === 'hit')
    .sort((a, b) => (b.prob || 0) - (a.prob || 0))[0]
  return (
    <div style={{ background: T.greenDim, border: `1px solid ${T.green}`, borderRadius: 13, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: T.green, letterSpacing: '0.06em', marginBottom: 6 }}>✓ WEEK {week} GRADED</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
        <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 26, color: T.green, lineHeight: 1 }}>
          {graded ? `${rec.wins}-${rec.losses}` : '—'}</span>
        <span style={{ fontSize: 13, color: T.muted2 }}>{rec.hit_rate != null ? `${rec.hit_rate}% · featured plays` : 'settled'}</span>
      </div>
      {best && <div style={{ fontSize: 11.5, color: T.muted2, marginTop: 6, lineHeight: 1.4 }}>
        Best hit: <b style={{ color: T.text }}>{best.name}</b> {best.market === 'Anytime TD' ? 'scored' : `went ${best.actual}`}.</div>}
      <button onClick={() => setView && setView('record')} style={{ width: '100%', marginTop: 11, padding: '9px 0', cursor: 'pointer',
        fontSize: 11.5, fontWeight: 800, color: '#04140D', background: T.green, border: 'none', borderRadius: 9 }}>
        See the full card →
      </button>
    </div>
  )
}

// A quiet "what's next" note for the recap face, so the app feels alive between slates.
function NextUp({ week }) {
  return (
    <div style={{ background: T.card, border: `1px dashed ${T.border2}`, borderRadius: 12, padding: '11px 13px', marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: T.faint, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Next up</div>
      <div style={{ fontSize: 12.5, color: T.muted2, lineHeight: 1.4 }}>
        Week {(week || 1) + 1} board opens as lines drop — check back midweek for the next top read.
      </div>
    </div>
  )
}

// ── Time-aware today strip — the top of the home, one of three faces:
//   pre    → the slate + a lock countdown to the next kickoff
//   live   → a live "games in progress" banner (the tracker sits right below)
//   recap  → "Week N graded"
function TodayStrip({ phase, games, week, results }) {
  const gs = games || []
  const kt = g => (g.kickoff ? Date.parse(g.kickoff) : Infinity)
  const upcoming = gs.filter(g => g.state === 'upcoming').sort((a, b) => kt(a) - kt(b))
  const done = gs.filter(g => g.state === 'final').length
  const live = gs.filter(g => g.state === 'live').length

  const fmtCountdown = ms => {
    if (!isFinite(ms) || ms <= 0) return null
    const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4)
    return h >= 1 ? `${h}h ${m}m` : `${m}m`
  }

  if (phase === 'live') {
    return (
      <div style={{ background: 'linear-gradient(120deg,#08202b,#0b1119)', border: `1px solid #0e4b63`,
        borderRadius: 12, padding: '11px 13px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 900 }}>Week {week} · Live</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.blue, background: T.blueDim, border: '1px solid #0e4b63', borderRadius: 6, padding: '3px 8px' }}>
          ● {live} game{live > 1 ? 's' : ''} in progress</span>
      </div>
    )
  }
  if (phase === 'recap') {
    return (
      <div style={{ background: 'linear-gradient(120deg,#0c2418,#0b1119)', border: `1px solid ${T.greenMid}`,
        borderRadius: 12, padding: '11px 13px', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 900 }}>✓ Week {week} graded</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Every featured play settled at the posted line</div>
      </div>
    )
  }
  // pre
  const nx = upcoming[0]
  const countdown = nx ? fmtCountdown(kt(nx) - Date.now()) : null
  const nxDay = nx ? (nx.day || nx.weekday || '') : ''
  const sameDay = nx ? upcoming.filter(g => (g.weekday || g.day) === (nx.weekday || nx.day)).length : 0
  const total = gs.length
  // Progress toward the next kickoff over a 48h pre-window, just for a sense of "soon".
  const pctToKick = nx && isFinite(kt(nx)) ? Math.max(3, Math.min(100, 100 - ((kt(nx) - Date.now()) / (48 * 3.6e6)) * 100)) : 0
  return (
    <div style={{ background: 'linear-gradient(120deg,#0c2418,#0b1119)', border: `1px solid ${T.greenMid}`,
      borderRadius: 12, padding: '11px 13px', marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 900 }}>Week {week}{nxDay ? ` · ${nxDay}` : ''}</div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
        {total} game{total !== 1 ? 's' : ''}
        {nx && countdown ? <> · {sameDay > 1 ? `${sameDay} games ` : 'next '}kick{sameDay > 1 ? 'off' : 's'} in <b style={{ color: T.muted2 }}>{countdown}</b></> : ''}
        {done ? ` · ${done} final` : ''}
      </div>
      {nx && countdown && (
        <div style={{ height: 4, background: '#0a1a12', borderRadius: 3, marginTop: 9, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pctToKick}%`, background: T.green, borderRadius: 3 }} />
        </div>
      )}
    </div>
  )
}

// Signals — the NFL home's curated angle-card set. Backed by GET /nfl/signals so the
// selection logic (game environment + defense-vs-position softness) lives server-side
// and is shared with other tabs and push notifications. Each card self-hides when empty.
function SigPlay({ p, gameFor, onGame }) {
  return (
    <button onClick={() => onGame && onGame(gameFor(p.game_id))} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: T.card2, border: 'none', borderRadius: 8, padding: '8px 10px', cursor: onGame ? 'pointer' : 'default', color: T.text, textAlign: 'left' }}>
      <span style={{ minWidth: 0, overflow: 'hidden' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{p.name} <span style={{ color: T.muted, fontWeight: 500 }}>{p.pos} · {p.team}{p.opp ? ` vs ${teamNick(p.opp)}` : ''}</span></span>
        <span style={{ fontSize: 11, color: T.muted2 }}>{playText(p.market, p.line)}</span>
        {p.matchup && <span style={{ fontSize: 10.5, color: T.green, display: 'block', marginTop: 1 }}>◆ {p.matchup}</span>}
      </span>
      <span style={{ fontFamily: T.mono, fontWeight: 800, color: probColor(p.prob || 0), flexShrink: 0 }}>{pct(p.prob || 0)}</span>
    </button>
  )
}

function SignalCard({ icon, title, accent, subtitle, plays, gameFor, onGame }) {
  if (!plays || !plays.length) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${accent}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, letterSpacing: '0.03em', marginBottom: 5 }}>{icon} {title}</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>{subtitle}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {plays.map((p, i) => <SigPlay key={i} p={p} gameFor={gameFor} onGame={onGame} />)}
      </div>
    </div>
  )
}

// Angles tonight — the FUN layer: birthdays on game day, historical storylines (Katrina →
// Saints), and "faces his former team" revenge spots. Flagged "just for fun, not edge" so it
// never muddies the honest board. Self-hides when the week has no angles.
function AnglesCard({ season, week, onGame }) {
  const [d, setD] = useState(null)
  useEffect(() => { let a = true; getNFLAngles(season, week).then(r => { if (a) setD(r) }); return () => { a = false } }, [season, week])
  const games = (d?.games || []).filter(g => g.birthdays?.length || g.events?.length || g.revenge?.length)
  if (!games.length) return null
  const open = gid => onGame && onGame(gid)
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.pink || '#e879b9', letterSpacing: '0.03em', marginBottom: 3 }}>🎟️ ANGLES THIS WEEK</div>
      <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 10, lineHeight: 1.4 }}>Storylines for fun — birthdays, anniversaries, revenge spots. <b style={{ color: T.muted }}>Not model edge.</b></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {games.map((g, i) => (
          <button key={i} onClick={() => open(g.game_id)} style={{ textAlign: 'left', background: T.card2, border: 'none', borderRadius: 9, padding: '9px 11px', cursor: onGame ? 'pointer' : 'default', color: T.text }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.muted2, marginBottom: 5 }}>{g.matchup}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(g.events || []).map((e, j) => (
                <span key={`e${j}`} style={{ fontSize: 11, fontWeight: 700, background: '#f5a52418', border: '1px solid #f5a52455', color: T.amber, borderRadius: 999, padding: '3px 9px' }}>
                  🕯️ {e.label}{e.years_ago ? ` · ${e.years_ago}y` : ''}</span>
              ))}
              {(g.revenge || []).map((r, j) => (
                <span key={`r${j}`} style={{ fontSize: 11, fontWeight: 700, background: '#5db4e618', border: '1px solid #5db4e655', color: '#5db4e6', borderRadius: 999, padding: '3px 9px' }}>
                  ⚔️ {r.player} vs former {r.former_team}</span>
              ))}
              {(g.birthdays || []).map((b, j) => (
                <span key={`b${j}`} style={{ fontSize: 11, fontWeight: 700, background: '#00d97e14', border: `1px solid ${T.greenMid}`, color: T.green, borderRadius: 999, padding: '3px 9px' }}>
                  🎂 {b.player}{b.turning ? ` (${b.turning})` : ''}</span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function ShootoutCard({ sh, gameFor, onGame }) {
  if (!sh) return null
  const hot = sh.total >= 50
  const modelOver = sh.model_total != null && sh.total != null && sh.model_total > sh.total
  return (
    <div style={{ background: T.card, border: `1px solid ${T.orange}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.orange, letterSpacing: '0.03em' }}>{hot ? '🌪️ SHOOTOUT' : '🔥 HIGH-SCORING SPOT'}</span>
        <span style={{ fontSize: 11, color: T.muted }}>highest total on the board</span>
      </div>
      <button onClick={() => onGame && onGame(gameFor(sh.game_id))} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: onGame ? 'pointer' : 'default', color: T.text, padding: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>
          {sh.away} @ {sh.home}
          <span style={{ fontSize: 13, color: T.orange, fontWeight: 800, marginLeft: 8, fontFamily: T.mono }}>O/U {sh.total}</span>
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2, lineHeight: 1.4 }}>
          {sh.model_total != null && <>Our model projects <b style={{ color: T.text }}>{sh.model_total}</b>{modelOver ? <b style={{ color: T.green }}> · leans Over too</b> : ''}. </>}
          The game where overs stack — passing, receiving, and TD scoring all get a lift. Research only.
        </div>
      </button>
      {sh.overs && sh.overs.length > 0 && (
        <div style={{ marginTop: 9, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sh.overs.map((p, i) => <SigPlay key={i} p={p} gameFor={gameFor} onGame={onGame} />)}
        </div>
      )}
    </div>
  )
}

function StackCard({ st, gameFor, onGame }) {
  if (!st) return null
  const leg = (p) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: T.card2, borderRadius: 8, padding: '8px 10px' }}>
      <span style={{ fontSize: 12.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{p.name}</b> <span style={{ color: T.muted }}>{playText(p.market, p.line)}</span></span>
      <span style={{ fontFamily: T.mono, fontWeight: 800, color: probColor(p.prob || 0), flexShrink: 0 }}>{pct(p.prob || 0)}</span>
    </div>
  )
  return (
    <div style={{ background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.green, letterSpacing: '0.03em', marginBottom: 5 }}>🎯 SAME-GAME STACK</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>
        {st.qb.team} in {st.away} @ {st.home} (O/U {st.total}) — a QB and his top target move together. Research only.
      </div>
      <button onClick={() => onGame && onGame(gameFor(st.game_id))} style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', background: 'transparent', border: 'none', cursor: onGame ? 'pointer' : 'default', color: T.text, padding: 0 }}>
        {leg(st.qb)}
        <div style={{ textAlign: 'center', fontSize: 10, color: T.muted, letterSpacing: '0.1em' }}>+</div>
        {leg(st.rec)}
      </button>
    </div>
  )
}

function StormCard({ storm, gameFor, onGame }) {
  if (!storm || !storm.length) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${T.orange}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.orange, letterSpacing: '0.03em', marginBottom: 5 }}>🌪️ TD STORM</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>
        Anytime-TD spots where it all lines up — the model likes him to score, his offense is expected to score, and the defense is soft to his TDs. Research only.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {storm.map((p, i) => (
          <button key={i} onClick={() => onGame && onGame(gameFor(p.game_id))} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: T.card2, border: 'none', borderRadius: 8, padding: '8px 10px', cursor: onGame ? 'pointer' : 'default', color: T.text, textAlign: 'left' }}>
            <span style={{ minWidth: 0, overflow: 'hidden' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{p.name} <span style={{ color: T.muted, fontWeight: 500 }}>{p.pos} · {teamNick(p.team)} vs {teamNick(p.opp)}</span></span>
              {p.matchup && <span style={{ fontSize: 10.5, color: T.orange, display: 'block', marginTop: 1 }}>◆ {p.matchup}</span>}
            </span>
            <span style={{ fontFamily: T.mono, fontWeight: 800, color: tdColor(p.prob || 0), flexShrink: 0 }}>{pct(p.prob || 0)} <span style={{ fontSize: 10, color: T.muted }}>TD</span></span>
          </button>
        ))}
      </div>
    </div>
  )
}

function Signals({ season, week, games, onGame }) {
  const [sig, setSig] = useState(null)
  useEffect(() => { let a = true
    getNFLSignals(season, week).then(r => { if (a) setSig(r) })
    return () => { a = false } }, [season, week])
  if (!sig) return null
  const gmap = {}
  ;(games || []).forEach(g => { gmap[g.game_id] = g })
  const gameFor = gid => gmap[gid] || { game_id: gid }
  const outlook = sig.td_outlook
  const has = sig.shootout || (sig.air_raid && sig.air_raid.length) || (sig.ground_control && sig.ground_control.length) || sig.stack || (sig.td_storm && sig.td_storm.length)
  if (!has && !(outlook && outlook.total)) return null
  return (
    <>
      {outlook && outlook.total ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: T.card, border: `1px solid ${T.border2}`, borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
          <span style={{ fontSize: 22 }}>🏈</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>~{outlook.total} touchdowns projected this week</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>across {outlook.games} games · ~{outlook.per_game}/game · model expectation</div>
          </div>
        </div>
      ) : null}
      {has && (<>
      <SectionLabel>⚡ Signals</SectionLabel>
      <StormCard storm={sig.td_storm} gameFor={gameFor} onGame={onGame} />
      <ShootoutCard sh={sig.shootout} gameFor={gameFor} onGame={onGame} />
      <SignalCard icon="🏈" title="AIR RAID" accent={T.purple} gameFor={gameFor} onGame={onGame}
        subtitle="Pass-catchers in throw-heavy spots — high totals, trailing scripts, and soft coverages, where receiving yards inflate." plays={sig.air_raid} />
      <SignalCard icon="🐢" title="GROUND CONTROL" accent={T.amber} gameFor={gameFor} onGame={onGame}
        subtitle="Backfields in positive game scripts — favorites and soft run fronts who grind out the clock on the ground." plays={sig.ground_control} />
      <StackCard st={sig.stack} gameFor={gameFor} onGame={onGame} />
      </>)}
    </>
  )
}

// Scheme watch — teams on THIS week's slate with a new coordinator (operator-set) or a
// scheme shift detected from play data. A research nugget: last year's tendencies may not
// hold. Self-hides when no slate team is flagged. Reads GET /nfl/coaching.
function SchemeWatch({ season, games }) {
  const [c, setC] = useState(null)
  useEffect(() => { let a = true; getNFLCoaching(season).then(r => { if (a) setC(r) }); return () => { a = false } }, [season])
  if (!c) return null
  const slate = new Set((games || []).flatMap(g => [g.away?.abbr, g.home?.abbr].filter(Boolean)))
  const flags = {}
  const add = (teams, unit, kind) => (teams || []).forEach(t => {
    if (slate.has(t) && !flags[`${t}:${unit}`]) flags[`${t}:${unit}`] = { team: t, kind } })
  add(c.coordinator_changes?.off, 'off', 'New OC')
  add(c.coordinator_changes?.def, 'def', 'New DC')
  add(c.detected_from_play_data?.off, 'off', 'Scheme shift · offense')
  add(c.detected_from_play_data?.def, 'def', 'Scheme shift · defense')
  const list = Object.values(flags)
  if (!list.length) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${T.amber}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.amber, letterSpacing: '0.03em', marginBottom: 5 }}>🧭 SCHEME WATCH</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>
        New coordinators or detected scheme shifts on this week's slate — last season's tendencies may not hold. Research only.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {list.map((f, i) => (
          <span key={i} style={{ fontSize: 11, fontWeight: 700, color: T.muted2, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 7, padding: '4px 8px' }}>
            <b style={{ color: T.text }}>{f.team}</b> · {f.kind}
          </span>
        ))}
      </div>
    </div>
  )
}

// Market beatability badge — SHARP (we beat the book here) / EFFICIENT (market's efficient,
// lean off) / NEUTRAL, from live settled results. BUILDING (not enough graded plays yet) shows
// nothing so a thin market never wears a verdict it hasn't earned.
const MKT_GRADE = {
  SHARP:     { label: '● SHARP',     c: T.green,  bg: 'rgba(0,217,126,.12)' },
  EFFICIENT: { label: 'EFFICIENT',   c: '#ff8f6b', bg: 'rgba(255,143,107,.10)' },
  NEUTRAL:   { label: 'NEUTRAL',     c: T.muted2, bg: T.card2 },
}
function MarketGrade({ edge, showBook }) {
  const g = edge && MKT_GRADE[edge.grade]
  if (!g) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', color: g.c, background: g.bg,
        border: `1px solid ${g.c}55`, borderRadius: 6, padding: '2px 6px', whiteSpace: 'nowrap' }}>{g.label}</span>
      {showBook && edge.book_hit != null && (
        <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono }}>{edge.book_hit}% book{edge.mean_clv != null ? ` · CLV ${edge.mean_clv > 0 ? '+' : ''}${edge.mean_clv}` : ''}</span>
      )}
    </span>
  )
}

// Across-the-board — every market at a glance: its beatability grade (do we beat the book here?),
// best play + how many strong bets. SHARP markets are pushed to the top so the beatable markets
// lead. Tapping a row deep-links into that market's rankings (not the default touchdowns).
const _MKT_ORDER = { SHARP: 0, NEUTRAL: 1, BUILDING: 2, EFFICIENT: 3 }
// Calibration honesty badge — turns the live record into a per-market trust signal: ✓ Calibrated
// (shown % ≈ what it hits), ⚠ Runs hot (overstated, lean light), ◆ Underrated (we're too low).
// "building"/"fair" render nothing. The thing no competitor shows: where our own numbers are soft.
const CAL_BADGE = {
  calibrated: { label: '✓ Calibrated', fg: T.green, bg: 'rgba(0,217,126,.14)', bd: 'rgba(0,217,126,.5)' },
  hot:        { label: '⚠ Runs hot',   fg: T.amber, bg: 'rgba(245,165,36,.14)', bd: 'rgba(245,165,36,.5)' },
  sharp:      { label: '◆ Underrated', fg: T.blue,  bg: 'rgba(78,168,255,.14)', bd: 'rgba(78,168,255,.5)' },
}
export function CalBadge({ cal }) {
  const s = cal && CAL_BADGE[cal.status]
  if (!s) return null
  const title = cal.shown != null ? `At ${cal.shown}% shown, this market has hit ${cal.hit}% this season (${cal.n} graded plays)` : ''
  return <span title={title} style={{ fontSize: 10, fontWeight: 800, color: s.fg, background: s.bg,
    border: `1px solid ${s.bd}`, borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap' }}>{s.label}</span>
}

function MarketSurvey({ byMarket, calByMarket, onMarket }) {
  const anyGraded = NFL_MARKETS.some(mk => (byMarket[mk.key]?.edge?.grade || 'BUILDING') !== 'BUILDING')
  const markets = anyGraded
    ? [...NFL_MARKETS].sort((a, b) =>
        (_MKT_ORDER[byMarket[a.key]?.edge?.grade] ?? 2) - (_MKT_ORDER[byMarket[b.key]?.edge?.grade] ?? 2))
    : NFL_MARKETS
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
      {markets.map((mk, i) => {
        const d = byMarket[mk.key] || {}
        const top = d.top
        return (
          <button key={mk.key} onClick={() => onMarket && onMarket(mk.key)} style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '11px 14px', border: 'none', borderTop: i ? `1px solid ${T.border}` : 'none', background: 'transparent', cursor: 'pointer', color: T.text }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>{mk.label}
                <span style={{ fontSize: 11, color: T.green, fontWeight: 700 }}>{d.strong || 0} strong</span>
                <MarketGrade edge={d.edge} />
                <CalBadge cal={calByMarket?.[mk.key]} /></div>
              {top && <div style={{ fontSize: 11, color: T.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{top.full_name} · {top.team} {top.opp ? `vs ${teamNick(top.opp)}` : ''}</div>}
            </div>
            {top && (() => {
              const priced = top.book_line != null && top.book_prob_over != null
              const shown = shownProb(top)
              return (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: T.mono, fontWeight: 800, color: playColor(shown, top) }}>{pct(shown)}</div>
                  <div style={{ fontSize: 10, color: T.muted }}>{priced ? 'to hit · book line' : 'to hit'}</div>
                </div>
              )
            })()}
          </button>
        )
      })}
    </div>
  )
}

// Sharpest Spots — the slate's best-VALUE plays, ranked by the backend's composite (edge +
// in-week CLV/steam + market beatability), NOT raw hit prob. Each row leads with the edge and
// the one-line "why", so it reads as "here's the real value tonight and the reason", distinct
// from the conviction (Top Plays) and chalk boards. Tap to open the full player breakdown.
function SharpestSpots({ spots, onPlayer }) {
  return (
    <div>
      {spots.map((s, i) => {
        const isTD = s.market === 'Anytime TD'
        const bet = isTD ? 'Anytime TD' : `${playText(s.market, s.line)}`
        const open = () => onPlayer && s.gsis_id && onPlayer({ gsis_id: s.gsis_id, full_name: s.name,
          name: s.name, pos: s.pos, team: s.team, opp: s.opp, market: s.market, line: s.line,
          prob_over: s.hit_prob, status: 'active' })
        return (
          <button key={i} onClick={open} style={{ display: 'flex', width: '100%', textAlign: 'left',
            alignItems: 'center', gap: 10, background: T.card, border: `1px solid ${T.border}`,
            borderRadius: 12, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', color: T.text }}>
            <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 13, color: T.green, width: 22, flexShrink: 0 }}>#{i + 1}</span>
            <Avatar url={s.headshot} name={s.name} team={s.team} size={38} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                <MarketGrade edge={{ grade: s.market_grade }} />
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ color: T.muted2, fontWeight: 700 }}>{bet}</span> · {s.team}{s.opp ? ` vs ${teamNick(s.opp)}` : ''}
                {s.book_over_odds != null ? <> · {bookName(s.book_used)} {fmtOdds(s.book_over_odds)}</> : null}
              </div>
              {s.why?.length ? <div style={{ fontSize: 10, color: T.green, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>✓ {s.why.join(' · ')}</div> : null}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 15, color: T.green }}>+{s.edge_pp}<span style={{ fontSize: 10, fontWeight: 700 }}>pp</span></div>
              <div style={{ fontFamily: T.mono, fontSize: 10.5, color: playColor(s.hit_prob || 0, s) }}>{pct(s.hit_prob || 0)}</div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// Pay Dirt entry — the front-page door to the TD Lab's red-zone scoring board. A bold,
// tappable card so users find the touchdown feature from Home. Red accent to match it.
// Board freshness stamp — tells the user which week they're looking at, when it was last built,
// and the game states, and warns when a shown week is STALE vs the live week (the Week-4-on-Monday
// confusion). Self-fetching; hides until the slate loads.
function _agoStr(dt) {
  const s = Math.max(0, (Date.now() - dt.getTime()) / 1000)
  if (s < 90) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}
function FreshnessStamp({ season, week }) {
  const [d, setD] = useState(null)
  useEffect(() => { let a = true
    getNFLSlate(season, week).then(r => { if (a) setD(r) }).catch(() => {})
    return () => { a = false } }, [season, week])
  const games = (d && d.games) || []
  if (!games.length) return null
  const final = games.filter(g => g.state === 'final').length
  const live = games.filter(g => g.state === 'live').length
  const upc = games.length - final - live
  const cw = d.current_week
  const stale = cw && week !== cw
  const allFinal = final === games.length
  const built = d.built_at ? new Date(d.built_at) : null
  const ago = built && !isNaN(built) ? _agoStr(built) : null
  let tone = T.green, msg = `Week ${week} board`
  if (stale) { tone = T.amber; msg = `Showing Week ${week} · Week ${cw} is the live week` }
  else if (live) { tone = T.blue || T.green; msg = `Week ${week} · live now` }
  else if (allFinal) { tone = T.muted2; msg = `Week ${week} · final` }
  const parts = []
  if (upc) parts.push(`${upc} to play`)
  if (live) parts.push(`${live} live`)
  if (final) parts.push(`${final} final`)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: T.muted,
      background: T.card, border: `1px solid ${stale ? `${T.amber}66` : T.border}`, borderRadius: 9, padding: '7px 11px', marginBottom: 10 }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: tone, flexShrink: 0 }} />
      <span style={{ fontWeight: 800, color: stale ? T.amber : T.text }}>{msg}</span>
      {parts.length > 0 && <span style={{ color: T.faint }}>· {parts.join(' · ')}</span>}
      <span style={{ flex: 1 }} />
      {ago && <span style={{ color: T.faint }}>updated {ago}</span>}
    </div>
  )
}

// Today's Best — the tight shortlist (post-calibration gating): the model's shortest list of
// its strongest reads. Self-fetching; hides until there are at least a few recommended plays.
function TodaysFive({ season, week, onPlayer }) {
  const [plays, setPlays] = useState(null)
  useEffect(() => { let a = true; setPlays(null)
    getNFLToday(season, week, 5).then(d => { if (a) setPlays(d?.plays || []) }).catch(() => { if (a) setPlays([]) })
    return () => { a = false } }, [season, week])
  if (!plays || plays.length < 3) return null
  return (
    <div style={{ marginBottom: 14 }}>
      <SectionLabel>⭐ Today's Best · the tight list</SectionLabel>
      <div style={{ fontSize: 10.5, color: T.muted, margin: '0 2px 8px', lineHeight: 1.5 }}>
        One play per player across every market — the best balance of <b style={{ color: T.text }}>chance to hit</b> and price. Calibrated · research, not a bet.
      </div>
      {plays.map((e, i) => <CompactPickRow key={e.gsis_id} e={e} rank={i + 1} onOpen={onPlayer} />)}
    </div>
  )
}

function PayDirtButton({ setView, season }) {
  const RED = '#ff5a52'
  const [wk, setWk] = useState(null)   // latest graded Pay Dirt week, for the highlight chip
  useEffect(() => { let a = true
    getNFLRedZoneRecord(season).then(d => { if (a) setWk((d?.by_week || [])[0] || null) }).catch(() => {})
    return () => { a = false } }, [season])
  const hot = wk && wk.plays >= 5 && (wk.hit_rate || 0) >= 60
  return (
    <button onClick={() => setView && setView('tdlab')} style={{ display: 'flex', width: '100%',
      alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left', color: T.text,
      background: 'linear-gradient(120deg,#241012,#0b1119)', border: `1px solid ${RED}66`,
      borderRadius: 13, padding: '13px 15px', marginBottom: 12 }}>
      <span style={{ fontSize: 26, flexShrink: 0 }}>🏈</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15.5, fontWeight: 900, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          Pay Dirt <span style={{ fontSize: 11, fontWeight: 700, color: RED }}>· Touchdowns</span>
          {hot && <span style={{ fontSize: 10.5, fontWeight: 900, color: '#04140D', background: T.green, borderRadius: 999, padding: '1px 8px' }}>🎯 {wk.wins}-{wk.losses} Wk {wk.week}</span>}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2, lineHeight: 1.4 }}>
          {hot ? `Hit ${wk.hit_rate}% to score last week — the week's best red-zone TD setups, with the best price.`
               : 'The week’s best red-zone touchdown setups — role, matchup, best price + a graded record.'}</div>
      </div>
      <span style={{ color: RED, fontWeight: 900, fontSize: 20, flexShrink: 0 }}>→</span>
    </button>
  )
}

// Home — the command center, now with three time-aware faces (before kickoff / live /
// recap). Leads with the answer — the model's top read + the receipts to trust it — then
// keeps the full board's depth (signals, every market, top plays, games) below.
// The first choice on open: let the model generate a ranked list (Play Lab), or build it
// yourself by jumping straight to a stat. Founder call: "do you wanna build a play or generate one".
function BuildOrGenerate({ setView, onMarket }) {
  const build = [
    ['Touchdowns', () => setView('touchdowns')],
    ['Receptions', () => onMarket ? onMarket('receptions') : setView('players')],
    ['Rec Yds', () => onMarket ? onMarket('rec_yards') : setView('players')],
    ['Rush Yds', () => onMarket ? onMarket('rush_yards') : setView('players')],
    ['Pass Yds', () => onMarket ? onMarket('pass_yards') : setView('players')],
    ['Pass TDs', () => onMarket ? onMarket('pass_tds') : setView('players')],
    ['By game', () => setView('games')],
  ]
  return (
    <div style={{ margin: '4px 0 14px' }}>
      <button onClick={() => { track('home_choice', 'generate'); setView('playlab') }} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, cursor: 'pointer',
        textAlign: 'left', color: T.text, background: 'linear-gradient(120deg,#0e2a1d,#0a1119)', border: `1px solid ${T.green}`,
        borderRadius: 14, padding: '15px 15px' }}>
        <span style={{ fontSize: 28, flexShrink: 0 }}>🧪</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 17, fontWeight: 900 }}>Generate a play</span>
          <span style={{ display: 'block', fontSize: 12, color: T.muted2, marginTop: 2, lineHeight: 1.4 }}>
            Pick the games + a stat — get the highest-probability plays, ranked.</span>
        </span>
        <span style={{ flexShrink: 0, fontSize: 13, fontWeight: 900, color: '#04140D', background: T.green, borderRadius: 9, padding: '8px 12px' }}>Go</span>
      </button>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: '12px 13px', marginTop: 8 }}>
        <div style={{ fontSize: 13.5, fontWeight: 900 }}>🔎 Build it yourself <span style={{ fontSize: 11.5, fontWeight: 600, color: T.muted }}>· what are you looking for?</span></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
          {build.map(([lbl, go]) => (
            <button key={lbl} onClick={() => { track('home_choice', `build:${lbl}`); go() }} style={{ cursor: 'pointer', fontSize: 12, fontWeight: 800, padding: '7px 11px', borderRadius: 9,
              border: `1px solid ${T.border2}`, background: T.card2, color: T.text }}>{lbl}</button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function HomeTab({ season, week, setView, onNavigate, onMarket, onPlayer, onGame, onGuide, lensLink, gameFilter }) {
  const [byMkt, setByMkt] = useState(null)   // { key: {rows, top, strong} }
  const [games, setGames] = useState(null)
  const [results, setResults] = useState(null)  // This-Week featured results (live/recap)
  const [unavailable, setUnavailable] = useState(false)
  const [topN, setTopN] = useState(10)          // Top plays board size: 10 / 20 / 25
  const [mktFilter, setMktFilter] = useState('ALL')  // Top-plays sub-filter by prop type
  const [lens, setLens] = useState(lensLink?.lens || 'top')   // Top plays lens: top-rated | most likely (Locks) | unders (Fades)
  const lensRef = useRef(null)
  // "More this week" fold — closed by default; remembered per device; a deep link into the board opens it.
  const [more, setMore] = useState(() => { if (lensLink) return true; try { return localStorage.getItem('brkr_home_more') === '1' } catch (_) { return false } })
  useEffect(() => { try { localStorage.setItem('brkr_home_more', more ? '1' : '0') } catch (_) {} }, [more])
  const [lensJump, setLensJump] = useState(!!lensLink)   // deep link (My Board) → scroll the lens into view once loaded
  useEffect(() => { if (lensLink?.lens) { setLens(lensLink.lens); setMore(true); setLensJump(true) } }, [lensLink?.n])
  useEffect(() => { if (lensJump && lensRef.current) { lensRef.current.scrollIntoView({ block: 'start' }); setLensJump(false) } })
  const [spots, setSpots] = useState(null)      // Sharpest Spots — backend value composite
  const [glance, setGlance] = useState(false)   // Week at a Glance — swipeable per-game deck
  const [cal, setCal] = useState(null)          // per-market calibration status for the honesty badges

  useEffect(() => { let a = true
    getNFLMarketCalibration(season).then(r => { if (a) setCal(r?.markets || {}) }).catch(() => { if (a) setCal({}) })
    return () => { a = false } }, [season])

  useEffect(() => {
    let alive = true
    let timer = null
    const loadBoards = () => Promise.all([
      ...NFL_MARKETS.map(mk => getNFLLeaderboard(mk.key, season, week).then(r => [mk.key, r.rows || [], r._sample, r.market_edge])),
      getNFLSlate(season, week).then(g => ['__games', g.games || [], g._sample]),
    ]).then(entries => {
      if (!alive) return
      const map = {}, gm = []
      let anySample = false
      for (const entry of entries) {
        const [k, rows, sample] = entry
        if (sample) anySample = true
        if (k === '__games') { gm.push(...rows); continue }
        const sorted = [...rows].sort((a, b) => (b.prob_over || 0) - (a.prob_over || 0))
        const pe = rows.filter(p => p.edge_pct != null && p.edge_pct > 0)
          .sort((a, b) => (b.edge_pct || 0) - (a.edge_pct || 0))
        // r.market_edge: this market's live beatability grade (SHARP/NEUTRAL/EFFICIENT/BUILDING)
        map[k] = { rows: sorted, top: pe[0] || sorted[0], strong: sorted.filter(isStrongCase).length,
                   edge: entry[3] || null }
      }
      setByMkt(map); setGames(gm); setUnavailable(anySample)
    })
    const loadResults = () => getNFLResults(season, week, 'featured').then(r => {
      if (!alive) return
      setResults(r && !r._sample ? r : null)
      clearTimeout(timer)
      // Live: refresh the tracker AND the play-card boards so live progress ticks up.
      if (r && r.has_live) { loadBoards(); timer = setTimeout(loadResults, 30000) }
    })
    loadBoards()
    loadResults()
    getNFLSharpestSpots(season, week, 10).then(r => { if (alive) setSpots(r.spots || []) })
    return () => { alive = false; clearTimeout(timer) }
  }, [season, week])

  if (!byMkt || !games) return <Spinner />

  // Cross-market pools for the top read + top plays. VEGAS-FIRST: a real play is one
  // priced against an actual book line with value on the over (edge_pct > 0). Each row is
  // tagged with its market label so the hero can name the bet.
  const allPlays = NFL_MARKETS.flatMap(mk => (byMkt[mk.key]?.rows || [])
    .filter(r => !r.no_feature)   // owner "don't feature" (role change the feed missed) — off the ranked boards
    .map(r => ({ ...r, _mk: mk.label, _isTD: mk.key === 'anytime_td' })))
  const pricedEdge = allPlays.filter(p => p.edge_pct != null && p.edge_pct > 0)
    .sort((a, b) => (b.edge_pct || 0) - (a.edge_pct || 0))
  // Sort by the probability the rows DISPLAY (hit prob at the real line), not prob_over (the
  // flat-line volume proxy) — otherwise a 68% can sit above a 70%.
  const _hp = p => (p.hit_prob ?? p.prob_over) || 0
  const modelOnly = allPlays.filter(p => !(p.edge_pct != null && p.edge_pct > 0))
    .sort((a, b) => _hp(b) - _hp(a))
  // Teams whose game already kicked off/finished fall BEHIND still-playable plays.
  const doneTeams = new Set((games || []).filter(g => g.state === 'final')
    .flatMap(g => [g.away?.abbr, g.home?.abbr].filter(Boolean)))
  const playable = p => !doneTeams.has(p.team)
  const rank = arr => [...arr.filter(playable), ...arr.filter(p => !playable(p))]
  const ordered = rank([...pricedEdge, ...modelOnly])
  const best = rank(pricedEdge)[0] || rank(modelOnly.filter(isStrongCase))[0]
    || pricedEdge[0] || modelOnly.find(isStrongCase) || ordered[0]
  const topPlays = ordered.filter(p => p !== best).slice(0, 6)
  // Cross-market high-conviction set — the Elite / Best Bet / Strong plays from EVERY
  // market in one place (tiered by the same tierFromProb the cards badge with), so the
  // model's best plays are one glance on Home, not one market board at a time.
  const HIGH_TIERS = new Set(['ELITE', 'BEST BET', 'STRONG'])
  const tierOf = p => p.tier || tierFromProb((p.hit_prob != null ? p.hit_prob : p.prob_over) || 0, p.confidence || 0, p.edge_pct, p.prop_type)
  const TIER_ORD = { ELITE: 0, 'BEST BET': 1, STRONG: 2 }
  const elitePlays = ordered.filter(p => p !== best && HIGH_TIERS.has(tierOf(p)))
    .sort((a, b) => (TIER_ORD[tierOf(a)] - TIER_ORD[tierOf(b)])
      || (((b.hit_prob ?? b.prob_over) || 0) - ((a.hit_prob ?? a.prob_over) || 0)))
    .slice(0, 8)
  const showElite = elitePlays.length >= 2
  const featuredList = showElite ? elitePlays : topPlays
  // Biggest edges — the top +EV value plays across EVERY market, sorted by edge (the
  // Keon Coleman spot: soft book line vs the model). Surfaced on its own so a big edge at
  // a MODEST hit-prob isn't buried under the chalk in the conviction strip above. Deduped
  // against the hero + featured strip so nothing shows twice.
  const _ekey = p => `${p.gsis_id || p.full_name}:${p._mk}`
  const _shownEdge = new Set([best, ...featuredList].filter(Boolean).map(_ekey))
  const bigEdges = rank(pricedEdge).filter(p => p !== best && !_shownEdge.has(_ekey(p))).slice(0, 5)
  // Game order — finished games sink, the rest run chronologically by kickoff (Thu → MNF).
  const _kc = (a, b) => { const ka = a.kickoff || a.gameday || '', kb = b.kickoff || b.gameday || ''
    return ka === kb ? 0 : !ka ? 1 : !kb ? -1 : (ka < kb ? -1 : 1) }
  const homeGames = [...games].sort((a, b) =>
    ((a.state === 'final') - (b.state === 'final')) || _kc(a, b))

  // Which face? live if a game is on; recap once every game is final; else pre-slate.
  const anyLive = games.some(g => g.state === 'live')
  const allFinal = games.length > 0 && games.every(g => g.state === 'final')
  const phase = anyLive ? 'live' : allFinal ? 'recap' : 'pre'
  const hasResults = !!results && (results.plays || []).length > 0

  return (
    <div style={{ padding: '6px 16px 80px' }}>
      <FreshnessStamp season={season} week={week} />
      <BuildOrGenerate setView={setView} onMarket={onMarket} />
      <div style={{ margin: '-6px 0 14px' }}><SlipCheckButton season={season} week={week} onPlayer={onPlayer} /></div>

      {/* The front page: four headline tiles + the record, then everything else behind one
          "More this week" fold so a first-time user isn't buried (founder: "too overwhelming"). */}
      <WeekDashboard season={season} week={week} games={games} onPlayer={onPlayer} onGame={onGame} setView={setView} gameFilter={gameFilter}
        onOpenLocks={() => { setLens('locks'); setMore(true); setLensJump(true) }} />
      {/* Week at a Glance — swipe every game's 30-second rundown, tap to open. Kept on the front
          page (not behind "More this week") — the founder's favorite high-level preview. */}
      {games.length > 0 && (
        <button onClick={() => { track('home_glance', 'open'); setGlance(true) }} style={{ display: 'flex', width: '100%', alignItems: 'center',
          gap: 12, cursor: 'pointer', textAlign: 'left', color: T.text, background: 'linear-gradient(120deg,#0c2018,#0a1119)',
          border: `1px solid ${T.green}55`, borderRadius: 13, padding: '13px 15px', margin: '2px 0 12px' }}>
          <span style={{ fontSize: 24, flexShrink: 0 }}>📋</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 900 }}>Week {week} at a glance</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2, lineHeight: 1.4 }}>
              Swipe every game's 30-second rundown — scheme, soft spots & who to watch.</div>
          </div>
          <span style={{ color: T.green, fontWeight: 900, fontSize: 20, flexShrink: 0 }}>→</span>
        </button>
      )}
      {glance && <WeekAtAGlance season={season} week={week} games={games} onGame={onGame} onClose={() => setGlance(false)} />}
      <TrustBar setView={setView} />
      {phase === 'live' && hasResults && <LiveTracker results={results} onGame={onGame} />}
      {phase === 'recap' && hasResults && <RecapCard results={results} week={week} setView={setView} />}

      <button onClick={() => { if (!more) track('home_more', 'open'); setMore(m => !m) }} aria-expanded={more} style={{ display: 'flex', width: '100%', alignItems: 'center',
        justifyContent: 'space-between', gap: 10, cursor: 'pointer', textAlign: 'left', color: T.text, background: T.card,
        border: `1px solid ${T.border2}`, borderRadius: 12, padding: '12px 14px', margin: '4px 0 12px' }}>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 900 }}>{more ? 'Less' : 'More this week'}</span>
          {!more && <span style={{ display: 'block', fontSize: 11, color: T.muted, marginTop: 2, lineHeight: 1.4 }}>
            Full top-plays board · Ask BRKR · player search · signals · market survey · every game</span>}
        </span>
        <span style={{ color: T.green, fontWeight: 900, fontSize: 18, flexShrink: 0 }}>{more ? '▴' : '▾'}</span>
      </button>

      {more && <>
      <YourPlayers onPlayer={onPlayer} />
      <AskBrkr season={season} week={week} onPlayer={onPlayer} />
      {games.length > 0 && <TodayStrip phase={phase} games={games} week={week} results={results} />}

      {/* New-user guide — one tap to "how to find reads & analyze them" (opens the help sheet). */}
      {onGuide && (
        <button onClick={onGuide} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', cursor: 'pointer',
          background: T.card, border: `1px solid ${T.border2}`, borderRadius: 12, padding: '11px 13px', margin: '2px 0 12px', textAlign: 'left' }}>
          <span style={{ fontSize: 18 }}>📖</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>New here? How to read LINEBRKR</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>Where to find the reads + how to analyze them</div>
          </div>
          <span style={{ color: T.green, fontWeight: 800, fontSize: 16 }}>→</span>
        </button>
      )}

      {/* Quick tools — the most-asked boards, one tap from Home. Two rows of four (grid) so
          every label is readable at a glance instead of truncated into a cramped single row. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, margin: '2px 0 14px' }}>
        {[
          ['touchdowns', '🏈', 'Touchdowns', 'Who scores', () => setView('touchdowns')],
          ['sheets', '🔥', 'Matchup Sheets', 'Soft D + who faces', () => setView('sheets')],
          ['defense', '🛡️', 'Defense', 'Softest vs pos', () => (onNavigate ? onNavigate('defense') : setView('games'))],
          ['blowout', '💥', 'Blowouts', 'Big favs + backup RB', () => setView('blowout')],
          ['ladder', '🪜', 'Ladder', 'Yards to climb', () => setView('ladder')],
          ['record', '📑', 'Record', 'Every graded play', () => setView('record')],
          ['community', '🏟️', 'Community', 'Wall · board · roadmap', () => setView('community')],
          ['more', '🧰', 'All tools', 'Everything else', () => setView('more')],
        ].map(([id, icon, title, sub, go]) => (
          <button key={id} onClick={go} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'flex-start',
            background: T.card, border: `1px solid ${T.border}`, borderRadius: 13, padding: '13px 6px 11px',
            color: T.text, textAlign: 'center' }}>
            <div style={{ fontSize: 23, lineHeight: 1 }}>{icon}</div>
            <div style={{ fontSize: 12, fontWeight: 800, marginTop: 7, lineHeight: 1.2 }}>{title}</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 3, lineHeight: 1.25 }}>{sub}</div>
          </button>
        ))}
      </div>



      {/* Today's Best — the tight, calibrated shortlist, the model's strongest reads,
          high up so it's the first thing after the lead. */}
      <TodaysFive season={season} week={week} onPlayer={onPlayer} />

      {/* The model's top read — the hero (still-playable during a live slate) — with the
          trust bar right under it: answer first, then the receipts. */}
      {best && (
        <>
          <SectionLabel right="All plays →" onRight={() => setView('players')}>
            {pricedEdge.includes(best) ? '★ Biggest edge on the board' : "★ The model's top read"}{phase === 'pre' ? '' : ' · still to come'}
          </SectionLabel>
          <HeroRead e={best} onPlayer={onPlayer} />
        </>
      )}

      {/* Elite & Strong — the model's highest-conviction plays across EVERY market, in one
          place, right under the hero. Falls back to the top reads when a thin/early slate
          has no plays in the top tiers yet. */}
      {(() => {
        // The week's best RECOMMENDED plays across every market, in one ranked board sized by
        // the user (Top 10 / 20 / 25). Falls back to the featured strip on a thin/early slate.
        const pool = ordered.filter(p => HIGH_TIERS.has(tierOf(p)))
          .sort((a, b) => (TIER_ORD[tierOf(a)] - TIER_ORD[tierOf(b)]) || (_hp(b) - _hp(a)))
        const usingConviction = pool.length >= 2
        // Prop-type sub-filter — markets actually present in the pool (so we never show an empty
        // chip), ordered by the canonical market order. Filter applies BEFORE the Top-N slice,
        // so "Top 10 · Receptions" = the 10 best receptions plays.
        const marketsIn = NFL_MARKETS.filter(mk => pool.some(p => p.market === mk.label))
        const mf = marketsIn.some(mk => mk.label === mktFilter) ? mktFilter : 'ALL'
        const filtered = mf === 'ALL' ? pool : pool.filter(p => p.market === mf)
        const list = (usingConviction ? filtered : topPlays).slice(0, usingConviction ? topN : 6)
        // One board, three lenses — Locks ("most likely to cash") and Fades ("the unders") used to
        // be separate screens under More; they're the same question asked three ways.
        const lensBar = (
          <div ref={lensRef} role="tablist" aria-label="Board lens" style={{ scrollMarginTop: 120, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4,
            background: T.card2, border: `1px solid ${T.border}`, borderRadius: 11, padding: 3, margin: '14px 0 2px' }}>
            {[['top', 'Top rated'], ['locks', 'Most likely'], ['fades', 'Unders']].map(([k, lbl]) => {
              const on = lens === k
              return <button key={k} role="tab" aria-selected={on} onClick={() => { track('lens', k); setLens(k) }} style={{ cursor: 'pointer', border: 'none',
                borderRadius: 8, padding: '8px 0', fontSize: 12, fontWeight: 800,
                color: on ? '#04140D' : T.muted, background: on ? T.green : 'transparent' }}>{lbl}</button>
            })}
          </div>
        )
        if (lens === 'locks' || lens === 'fades') return (
          <>
            {lensBar}
            <SectionLabel right="All plays →" onRight={() => setView('players')}>
              {lens === 'locks' ? '🔒 Most likely to cash' : '⚠️ Overs to fade · the unders'}
            </SectionLabel>
            {lens === 'locks'
              ? <LocksTab season={season} week={week} onPlayer={onPlayer} embedded />
              : <FadesTab season={season} week={week} onPlayer={onPlayer} embedded />}
          </>
        )
        if (!usingConviction && !list.length) return lensBar
        return (
          <>
            {lensBar}
            <SectionLabel right="All plays →" onRight={() => setView('players')}>
              {usingConviction ? `🏆 Top ${topN} plays${mf !== 'ALL' ? ` · ${mf}` : ''}` : (phase === 'pre' ? 'Featured plays' : 'More featured plays')}
            </SectionLabel>
            {usingConviction && <div style={{ fontSize: 10.5, color: T.muted, margin: '-4px 2px 8px', lineHeight: 1.5 }}>
              Every top-rated play (Elite, Top Rated, Strong), ranked by rating, then by chance to hit.</div>}
            {usingConviction && (
              <>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  {[10, 20, 25].map(n => {
                    const on = topN === n
                    return <button key={n} onClick={() => setTopN(n)} style={{ flex: 1, padding: '7px 0',
                      borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 12,
                      border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
                      color: on ? T.green : T.muted }}>Top {n}</button>
                  })}
                </div>
                {marketsIn.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 8 }}>
                    {[{ key: 'ALL', label: 'All props' }, ...marketsIn.map(mk => ({ key: mk.label, label: mk.label }))].map(opt => {
                      const on = mf === opt.key
                      return <button key={opt.key} onClick={() => setMktFilter(opt.key)} style={{ flexShrink: 0, padding: '6px 11px',
                        borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 11.5, whiteSpace: 'nowrap',
                        border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
                        color: on ? T.green : T.muted }}>{opt.label}</button>
                    })}
                  </div>
                )}
              </>
            )}
            {list.length
              ? list.map((e, i) => <CompactPickRow key={`${mf}-${i}`} e={e} rank={i + 1} onOpen={onPlayer} />)
              : <div style={{ fontSize: 11, color: T.muted, padding: '10px 2px' }}>No {mf} plays in the recommended board yet.</div>}
            <div style={{ fontSize: 10, color: T.faint, marginTop: 2, marginBottom: 2, lineHeight: 1.4 }}>Tap any play for the full breakdown.</div>
            {usingConviction && mf === 'ALL' && pool.length < topN && (
              <div style={{ fontSize: 10.5, color: T.muted, marginTop: 4, lineHeight: 1.4 }}>
                {pool.length} recommended plays on the board so far — more populate as book lines post through the week.
              </div>
            )}
          </>
        )
      })()}

      {/* Prime Spots — players whose market-specific setup stacks multiple tailwinds (model +
          soft matchup + scoring game + edge). The "big game" setups, per market. */}
      <PrimeSpots season={season} week={week} onPlayer={onPlayer} limit={6} />

      {/* Sharpest Spots — the slate-wide best VALUE, ranked by the backend composite (edge +
          in-week CLV/steam + which markets we beat), not raw hit prob. Falls back to the
          frontend "biggest edges" strip early in the week before lines/steam exist. */}
      {spots && spots.length >= 2 ? (
        <>
          <SectionLabel right="All value →" onRight={() => setView('players')}>🎯 Sharpest Spots</SectionLabel>
          <div style={{ fontSize: 10.5, color: T.muted, margin: '0 2px 8px', lineHeight: 1.5 }}>
            Ranked by <b style={{ color: T.text }}>edge + market movement (CLV) + which markets we beat</b> — the real value tonight, not just the chalk.
          </div>
          <SharpestSpots spots={spots.slice(0, 8)} onPlayer={onPlayer} />
          <button onClick={() => shareNflSharpestSpots(season, week, 10)} style={{ display: 'block', width: '100%',
            marginTop: 2, padding: '10px 0', background: T.card, border: `1px solid ${T.border}`, borderRadius: 10,
            color: T.green, fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>
            📤 Share this board
          </button>
        </>
      ) : bigEdges.length >= 2 ? (
        <>
          <SectionLabel right="All value →" onRight={() => setView('players')}>
            💰 Biggest edges · model vs the market
          </SectionLabel>
          {bigEdges.map((e, i) => <CompactPickRow key={`edge-${i}`} e={e} rank={i + 1} onOpen={onPlayer} />)}
        </>
      ) : null}

      {/* Pay Dirt — front-page door into the red-zone TD board (TD Lab). */}
      <PayDirtButton setView={setView} season={season} />

      {/* Get pinged before kickoff — surfaced here (not just Record) so more users opt in. */}
      <NotifyCard />

      {phase === 'recap' && <NextUp week={week} />}

      <PlayerSearch season={season} week={week} onPlayer={onPlayer} />
      {unavailable && <div style={{ fontSize: 11, color: T.amber, marginBottom: 10 }}>● Live data unavailable right now — check back shortly.</div>}

      <Signals season={season} week={week} games={games} onGame={onGame} />
      <SchemeWatch season={season} games={games} />
      <AnglesCard season={season} week={week} onGame={onGame} />

      <SectionLabel right="All markets →" onRight={() => setView('players')}>Across the board</SectionLabel>
      {NFL_MARKETS.some(mk => (byMkt[mk.key]?.edge?.grade || 'BUILDING') !== 'BUILDING') && (
        <div style={{ fontSize: 10.5, color: T.muted, margin: '0 2px 8px', lineHeight: 1.5 }}>
          <b style={{ color: T.green }}>● SHARP</b> = we beat the book here (positive CLV / winning book-line record) · <b style={{ color: '#ff8f6b' }}>EFFICIENT</b> = market's tight, lean off. Beatable markets listed first.
        </div>
      )}
      <MarketSurvey byMarket={byMkt} calByMarket={cal} onMarket={onMarket || (() => setView('players'))} />


      <SectionLabel right="All games →" onRight={() => setView('games')}>
        Games to target{games.length ? ` · ${games.length}` : ''}
      </SectionLabel>
      {games.length
        ? homeGames.slice(0, 6).map(g => <GameCard key={g.game_id} game={g} onOpen={onGame} />)
        : <div style={{ color: T.muted, fontSize: 13, padding: 16 }}>No games scheduled for Week {week}.</div>}
      {games.length > 6 && (
        <button onClick={() => setView('games')} style={{ display: 'block', width: '100%', marginTop: 4, padding: '11px 0', background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, color: T.green, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          See all {games.length} games →
        </button>
      )}
      </>}
    </div>
  )
}
